import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BrandLockup } from "@/components/Wordmark";
import { media } from "@/lib/format";
import { useKiosk } from "./KioskContext";
import { CampaignSlideView } from "./CampaignMedia";

export const playable = (offers, place, online) =>
  offers.filter((o) => o.media && o.placements?.includes(place) && (place === "attract" ? online || o.media.type !== "youtube" : o.media.type === "image"));

const ModelSlide = ({ m, tx }) => (
  <motion.div key={m.slug} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.9 }}
    className="flex flex-col items-center text-center px-6">
    <p className="text-[#087568] font-semibold text-lg">{m.category}</p>
    <h2 className="font-display font-extrabold tracking-[-0.04em] text-6xl lg:text-[112px] pk:!text-[15vw] leading-[0.95]">{m.short}</h2>
    <p className="mt-4 text-xl pk:!text-[3.2vw] text-[#6E6E73]">{tx(m.tagline)}</p>
    <div className="relative mt-6 h-[38vh] w-[70vw] max-w-[820px] pk:!h-[40vh] pk:!w-[92vw] pk:!max-w-none">
      <div className="floor-glow absolute left-1/2 bottom-0 -translate-x-1/2 h-24 w-[80%]" />
      <img src={media(m.hero_image)} alt={m.name} className="attract-drift product-shadow relative h-full w-full object-contain" />
    </div>
  </motion.div>
);

export const Attract = ({ onWake }) => {
  const { data, tx, t, logPlay, online } = useKiosk();
  const items = useMemo(() => {
    const camps = playable(data.offers, "attract", online);
    if (!camps.length) return data.models.map((m) => ({ m }));
    return data.models.flatMap((m, i) => (i % 2 === 0 ? [{ c: camps[(i / 2) % camps.length] }, { m }] : [{ m }]));
  }, [data, online]);
  const [i, setI] = useState(0);
  const it = items[i % items.length];
  useEffect(() => {
    const h = setTimeout(() => setI((x) => x + 1), it.c && it.c.media.type !== "image" ? 15000 : 6500);
    return () => clearTimeout(h);
  }, [i, it]);
  useEffect(() => {
    if (it.c) logPlay("campaign", it.c.id, it.c.title?.en || "");
    else logPlay("attract", it.m.slug, it.m.name);
  }, [it, logPlay]);

  return (
    <motion.div data-testid="attract-mode" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
      onPointerDown={onWake} className="fixed inset-0 z-[60] bg-white flex flex-col items-center justify-between py-10 overflow-hidden cursor-pointer">
      <div className="relative z-10 rounded-full bg-white/85 px-5 py-2 backdrop-blur-xl"><BrandLockup size={34} /></div>
      <AnimatePresence mode="wait">
        {it.c ? (
          <motion.div key={`c-${it.c.id}-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9 }}
            className="absolute inset-0" data-testid="attract-campaign">
            <CampaignSlideView media={it.c.media} kicker={t("campaignKicker")} title={tx(it.c.title)} body={tx(it.c.body)} className="h-full w-full" textClass="pb-28 lg:pb-32 pk:!pb-40" />
          </motion.div>
        ) : <ModelSlide key={`m-${it.m.slug}`} m={it.m} tx={tx} />}
      </AnimatePresence>
      <p className="relative z-10 flex items-center gap-3 rounded-full bg-white/85 px-5 py-2 text-[#6E6E73] text-lg backdrop-blur-xl"><span className="h-2.5 w-2.5 rounded-full bg-[#0B8577] animate-pulse" />{t("touchToExplore")}</p>
    </motion.div>
  );
};
